import type { Block, Document, Fragment, Inline, NodeType, Text, TextRange } from '@gitbook/slate';
import { List } from '@gitbook/util-immutable';

type ChildItem = {
    child: NodeType;
    // Index of the child in its parent
    index: number;
};

type RangeItem = {
    // True if this represents the start of the given range
    isRangeStart: boolean;
    // True if this represents the end of the given range
    isRangeEnd: boolean;
    range: TextRange;
};

type Item = ChildItem | RangeItem;
type OrderedItem = Item & { order: number };
type ResultItem = Item | OrderedItem;

/** Mapping of key to their index in the node tree */
type NodeKeysOrder = Map<string, number>;

/**
 * Split the ranges in lists of relevant ranges for each child.
 * We process the decorations and selections at the same time to reduce
 * the amount of work as we potentially have to iterate over all children for both.
 */
export function getChildrenRanges(
    node: Document | Block | Fragment | Inline,
    decorations: List<TextRange>,
    selections: List<TextRange>
): [Array<List<TextRange>>, Array<List<TextRange>>] {
    const [orderedDecorations, orderedSelections] = orderDecorationsAndSelections(
        node,
        decorations,
        selections
    );

    return [groupRanges(orderedDecorations), groupRanges(orderedSelections)];
}

/**
 * Group and order the ranges.
 */
function groupRanges(orderedRanges: ResultItem[]): Array<List<TextRange>> {
    const activeRanges = new Set<TextRange>();
    const childrenRanges: Array<List<TextRange>> = [];

    // For each "child" item, we push along any previous
    // ranges that were added and not yet removed.
    orderedRanges.forEach((item) => {
        if ('range' in item) {
            if (item.isRangeStart) {
                // Item is a range start
                activeRanges.add(item.range);
            } else if (item.isRangeEnd) {
                // Item is a range end
                activeRanges.delete(item.range);
            }
        } else {
            // Item is a child node
            childrenRanges.push(List.create([...activeRanges]));
        }
    });

    return childrenRanges;
}

/**
 * Orders the children of provided node and its range endpoints (start, end)
 * so that ranges can be passed only to relevant children.
 */
function orderDecorationsAndSelections(
    node: Document | Block | Fragment | Inline,
    decorations: List<TextRange>,
    selections: List<TextRange>
): [ResultItem[], ResultItem[]] {
    // Create an array of all child nodes
    const childNodes = node.nodes.toArray();

    // If decorations and selections are empty, simply return the nodes children indexes
    if (decorations.isEmpty() && selections.isEmpty()) {
        const childrenItems = childNodes.map<ChildItem>((child, index) => ({
            child,
            index,
        }));

        return [childrenItems, childrenItems];
    }

    // Create a global order for all keys in the node once
    const keyOrders = createGlobalOrder(node);

    // Don't lookup the containing child for a start key multiple times as this is
    // a time expensive operation. We deduplicate the ranges startKeys and only
    // look them up once, then use startKeysToOrderMap while iterating on ranges.
    const allStartKeysSet = new Set(
        decorations
            .concat(selections)
            .map((range) => range.startKey!)
            .toArray()
    );

    const startKeysToOrderMap: NodeKeysOrder = new Map();
    for (const startKey of allStartKeysSet) {
        const startKeyOrder = keyOrders.get(startKey);

        const containingChildOrder =
            startKeyOrder === undefined
                ? 0
                : getContainingChildOrder(childNodes, keyOrders, startKey, startKeyOrder);

        startKeysToOrderMap.set(startKey, containingChildOrder);
    }

    // Process decorations
    const orderedDecorations = orderChildRanges(
        decorations,
        childNodes,
        keyOrders,
        startKeysToOrderMap
    );
    // Process selections
    const orderedSelections = orderChildRanges(
        selections,
        childNodes,
        keyOrders,
        startKeysToOrderMap
    );

    // Sort ranges and child by their `order` property
    return [orderedDecorations, orderedSelections];
}

/**
 * Order the children ranges by getting their order and sorting them by order.
 */
function orderChildRanges(
    ranges: List<TextRange>,
    childNodes: Array<Block | Inline | Text>,
    keyOrders: NodeKeysOrder,
    startKeysToOrderMap: NodeKeysOrder
): OrderedItem[] {
    // Process decorations
    const items: OrderedItem[] = childNodes.map((child, index) => ({
        child,
        index,
        order: keyOrders.get(child.key)!,
    }));

    // Add ranges start and end
    ranges.forEach((range) => {
        // TextRange start - Marks the start of a range.
        // It should be placed before the child or its parent with the according `startKey`.
        const containingChildOrder = startKeysToOrderMap.get(range.startKey!) as number;

        items.push({
            isRangeStart: true,
            isRangeEnd: false,
            order: containingChildOrder - 0.5,
            range,
        });

        // TextRange end - Marks the end of a ranges.
        // It should be placed after the child with the according `endKey`.
        const potentialEndKeyOrder = keyOrders.get(range.endKey!);
        const endKeyOrder =
            (typeof potentialEndKeyOrder === 'number'
                ? potentialEndKeyOrder
                : getLatestOrder(keyOrders)) + 0.5;

        items.push({
            isRangeStart: false,
            isRangeEnd: true,
            order: endKeyOrder,
            range,
        });
    });

    // Sort ranges and child by their `order` property
    return items.sort(byOrder);
}

/**
 * Map each node and fragment key to its global order.
 * @example
 *  document: 0
 *      block: 1
 *          text: 2
 *      block: 3
 *          text: 4
 */
function createGlobalOrder(node: Document | Block | Fragment | Inline): NodeKeysOrder {
    const keysOrder: NodeKeysOrder = new Map();
    keysOrder.set(node.key, 0);

    let index = 1;
    node.forEachDescendant(
        (child) => {
            keysOrder.set(child.key, index);
            index += 1;
        },
        { withFragments: true }
    );

    return keysOrder;
}

/**
 * Returns the last child order in a NodeKeysOrder map.
 */
function getLatestOrder(keyOrders: NodeKeysOrder): number {
    const entries = Array.from(keyOrders.entries());
    entries.sort((a, b) => a[1] - b[1]);
    return entries[entries.length - 1][1];
}

/**
 * Returns the key order of the child right before the given order.
 */
function getContainingChildOrder(
    children: Array<Block | Inline | Text>,
    keyOrders: NodeKeysOrder,
    key: string,
    keyOrder: number
): number {
    // Find the first child that is after the given key
    const nextChildIndex = children.findIndex((child) => {
        const subChildFound = 'nodes' in child ? child.hasDescendant(key) : false;
        if (subChildFound) {
            return true;
        }

        const childOrder = keyOrders.get(child.key) as number;
        return keyOrder <= childOrder;
    });

    if (nextChildIndex <= 0) {
        return 0;
    }

    const containingChild = children[nextChildIndex];
    return keyOrders.get(containingChild.key) as number;
}

/*
 * Compare two objects to sort them according to their .order property
 */
function byOrder(a: OrderedItem, b: OrderedItem) {
    if (a.order > b.order) {
        return 1;
    }

    if (a.order < b.order) {
        return -1;
    }

    return 0;
}
